import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { getMasteryBand } from './masteryBands';
interface SubjectModuleRowProps {
  variant: 'parent' | 'teacher';
  moduleId: string;
  moduleTitle: string;
  moduleIcon: React.ReactNode;
  mastery: number;
  trend: 'up' | 'down' | 'stable';
  trendDelta?: number;
  distribution?: {
    strong: number;
    solid: number;
    developing: number;
    attention: number;
    total: number;
  };
  practicedCount?: number;
  parentSubtitle?: string;
  isExpanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}
export function SubjectModuleRow({
  variant,
  moduleId,
  moduleTitle,
  moduleIcon,
  mastery,
  trend,
  trendDelta,
  distribution,
  practicedCount,
  parentSubtitle,
  isExpanded,
  onToggle,
  children
}: SubjectModuleRowProps) {
  const band = getMasteryBand(mastery);
  return <div className="bg-white border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" data-id="element-8717" data-mp="3tw5gdkxnnpk3">
      <div className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4 cursor-pointer" onClick={onToggle} role="button" tabIndex={0} aria-expanded={isExpanded} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle();
      }
    }} data-id="element-8718" data-mp="1jupm67rdoe3t">
        {/* Icon */}
        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${band.bgClass} ${band.textClass}`} data-id="element-8719" data-mp="113yhhjic5bvr">
          {moduleIcon}
        </div>

        {/* Title & Subtitle */}
        <div className="flex-1 min-w-0" data-id="element-8720" data-mp="1etifkc0z1kyq">
          <div className="font-bold text-slate-900 truncate" data-id="element-8721" data-mp="1eujzl5zythsl">{moduleTitle}</div>
          <div className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-8722" data-mp="henzfyv4aeuh">
            {variant === 'parent' ? <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5" data-id="element-8723" data-mp="mc60z46t1rsg">
                <span className={`font-bold ${band.textClass}`} data-id="element-8724" data-mp="1pnlhgph9di89">
                  {band.label}
                </span>
                <span className="text-slate-300" data-id="element-8725" data-mp="1knz7cowq8i3o">·</span>
                <span data-id="element-8726" data-mp="1ygfcpwd3os7r">{mastery}%</span>
                {parentSubtitle && <>
                    <span className="text-slate-300" data-id="element-8727" data-mp="1uetr9mmi35ly">·</span>
                    <span className="text-slate-400" data-id="element-8728" data-mp="1ufdb58vpo42m">{parentSubtitle}</span>
                  </>}
              </span> : <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5" data-id="element-8729" data-mp="11qfwrgumerf5">
                <span data-id="element-8730" data-mp="1fpxn40nkecqm">{practicedCount || 0} leerlingen</span>
                <span className="text-slate-300" data-id="element-8731" data-mp="1friittlm81q2">·</span>
                <span className={`font-bold ${band.textClass}`} data-id="element-8732" data-mp="1aqcojrcb46ef">
                  {mastery}% gemiddeld
                </span>
              </span>}
          </div>
        </div>

        {/* Progress / Distribution */}
        <div className="w-20 sm:w-28 md:w-32 flex-shrink-0 flex items-center justify-end" data-id="element-8733" data-mp="2m580h5jkjtra">
          {variant === 'parent' ? <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-8734" data-mp="2up4lnxldru2g">
              <motion.div initial={{
            width: 0
          }} animate={{
            width: `${mastery}%`
          }} className={`h-full rounded-full ${band.barClass}`} data-id="element-8735" data-mp="2yq6mujyuyg72" />
            </div> : distribution && distribution.total > 0 ? <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden flex" data-id="element-8736" data-mp="3iccrqhqmv75j">
              {distribution.strong > 0 && <div className="h-full bg-emerald-500" style={{
            width: `${distribution.strong / distribution.total * 100}%`
          }} title={`${distribution.strong} sterk`} data-id="element-8737" data-mp="33mor7iqa0civ" />}
              {distribution.solid > 0 && <div className="h-full bg-indigo-500" style={{
            width: `${distribution.solid / distribution.total * 100}%`
          }} title={`${distribution.solid} goed op weg`} data-id="element-8738" data-mp="4zswpk05b856" />}
              {distribution.developing > 0 && <div className="h-full bg-amber-500" style={{
            width: `${distribution.developing / distribution.total * 100}%`
          }} title={`${distribution.developing} aan het groeien`} data-id="element-8739" data-mp="1yibbgghu16dq" />}
              {distribution.attention > 0 && <div className="h-full bg-rose-500" style={{
            width: `${distribution.attention / distribution.total * 100}%`
          }} title={`${distribution.attention} aandacht nodig`} data-id="element-8740" data-mp="2md48cvc16slv" />}
            </div> : <div className="w-16 h-2 bg-slate-100 rounded-full" data-id="element-8741" data-mp="3hgzh1baq3swu" />}
        </div>

        {/* Trend */}
        <div className="hidden sm:flex w-16 flex-shrink-0 justify-end" data-id="element-8742" data-mp="3mgc15oa7mlvq">
          {trend === 'up' && <span className="flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded uppercase tracking-wider" data-id="element-8743" data-mp="3rdko026aze3b">
              <ArrowUpRight className="w-3 h-3" data-id="element-8744" data-mp="49xortrlajzg" />
              {trendDelta && trendDelta > 0 ? `+${trendDelta}%` : ''}
            </span>}
          {trend === 'down' && <span className="flex items-center gap-0.5 text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded uppercase tracking-wider" data-id="element-8745" data-mp="96egexxzzgha">
              <ArrowDownRight className="w-3 h-3" data-id="element-8746" data-mp="ix90pwroo603" />
              {trendDelta && trendDelta < 0 ? `${trendDelta}%` : ''}
            </span>}
        </div>

        {/* Chevron */}
        <div className="w-6 flex-shrink-0 flex justify-end text-slate-400" data-id="element-8747" data-mp="s0a6txgjtx3r">
          {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-8748" data-mp="2p0xw4mtp64w1" /> : <ChevronDown className="w-5 h-5" data-id="element-8749" data-mp="2k0rxgmp0fi4g" />}
        </div>
      </div>

      <AnimatePresence data-id="element-8750" data-mp="2jyyr78ib6zwz">
        {isExpanded && children && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden bg-slate-50/50 border-t border-slate-100" data-id="element-8751" data-mp="2xszu8egan935">
            {children}
          </motion.div>}
      </AnimatePresence>
    </div>;
}